import type { CSSProperties } from 'react';
import { Alert, Box, Icon as DSIcon, Tooltip } from '@ksyos/design-system';
import '@ksyos/react-dropzone-component/styles/filepicker.css';
import { Link as ReactRouterLink, useNavigate } from '@ksyos/router';
import classnames from 'classnames';
import 'rc-slider/assets/index.css';
import React from 'react';
import 'react-toastify/dist/ReactToastify.css';
import { styled } from 'styled-components';
import { getLanguage } from '@common/translate/translate';
import type { IFormContext } from './formContext';
import { getDisplayName } from '../../../common/utils/getDisplayName';
import { getGlobalize, translateKey } from '../../translate';
import { getValidationErrorTranslation } from '../../utils/getValidationErrorTranslation';
import { NullableTextInput } from '../fields/NullableText';
import { TextInput } from '../fields/Text';
import { Component } from './Component';
import { FormContextProvider } from './formContext';
import { NullableFormattedDate } from './NullableFormattedDate';
import { NullableFormattedDatetime } from './NullableFormattedDatetime';

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function Loading<T>({
    item,
    loaded,
}: {
    item: T | undefined | null;
    loaded: (item: T) => JSX.Element;
}): JSX.Element {
    if (item) {
        return loaded(item);
    } else {
        return <div>Loading...</div>;
    }
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function LoadingTwo<T1, T2>(props: {
    item1?: T1;
    item2?: T2;
    loaded: (item1: T1, item2: T2) => JSX.Element;
}): JSX.Element {
    if (props.item1 && props.item2) {
        return props.loaded(props.item1, props.item2);
    } else {
        return <div>Loading...</div>;
    }
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormContainer<ItemType>(props: {
    children: React.ReactNode;
    editable?: boolean;
    formContext?: IFormContext<ItemType>;
    onSubmit: () => void;
}) {
    return (
        <div className={classnames('form-container', { editable: props.editable })}>
            <Form {...props}>{props.children}</Form>
        </div>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function Form<ItemType>(props: {
    children: React.ReactNode;
    formContext?: IFormContext<ItemType>;
    formRef?: React.Ref<HTMLFormElement>;
    id?: string;
    name?: string;
    onSubmit: () => void;
    showOverlay?: boolean;
}) {
    const form = (
        <form
            className={props.showOverlay ? 'has-overlay' : ''}
            id={props.id}
            name={props.name}
            onSubmit={(e) => {
                e.preventDefault();
                props.onSubmit();
            }}
            ref={props.formRef}
        >
            {props.showOverlay && <div className="form-overlay"></div>}
            {props.children}
        </form>
    );

    if (props.formContext) {
        return <FormContextProvider value={props.formContext as IFormContext<any>}>{form}</FormContextProvider>;
    } else {
        return form;
    }
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormOverlayMessage(props: { children: React.ReactNode }) {
    return <div className="form-overlay__message">{props.children}</div>;
}

export interface IFormGroupProps {
    children: React.ReactNode;
    flex?: boolean;
    hasErrors?: boolean;
    margin?: boolean;
}
/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormGroup(props: IFormGroupProps) {
    let className = 'form-group';

    if (props.margin) {
        className += ' has-margin';
    }
    if (props.flex) {
        className += ' flex';
    }

    return <div className={classnames(className, props.hasErrors ? ' has-danger' : '')}>{props.children}</div>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormHeader(props: {
    actionButtons?: React.ReactElement<{ key: number }>[];
    children: React.ReactNode;
    isRequired?: boolean;
}) {
    return (
        <h2 className="form-header">
            {props.children}
            {props.isRequired && <span className="required">*</span>}
            {props.actionButtons && (
                <span className="float-right">
                    {props.actionButtons.map((actionButton, i) => React.cloneElement(actionButton, { key: i }))}
                </span>
            )}
        </h2>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormSubHeader(props: { children: React.ReactNode; isRequired?: boolean }) {
    return (
        <h3>
            {props.children}
            {props.isRequired && <span className="required">*</span>}
        </h3>
    );
}
/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormParagraph(props: {
    bold?: boolean;
    children: React.ReactNode;
    color?: 'muted' | 'primary';
    leading?: boolean;
}) {
    return (
        <p
            className={classnames(
                props.color ? `${props.color}` : '',
                props.leading ? 'lead' : '',
                props.bold ? 'font-weight-bold' : 'font-weight-normal'
            )}
        >
            {props.children}
        </p>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function UserTextParagraph(props: { children: React.ReactNode }) {
    return <p className="user-text">{props.children}</p>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormInlineGroup(props: { label?: string; value?: string | boolean | null }) {
    if (typeof props.value === 'string') {
        return (
            <span className="form-inline-group">
                {props.label ? `${props.label}:` : ''} {props.value}
            </span>
        );
    } else if (typeof props.value === 'boolean') {
        return (
            <span className="form-inline-group">
                {props.label}: <ShowYesNo value={props.value} />
            </span>
        );
    }

    return null;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormDivider(props: { children?: React.ReactNode; className?: string }) {
    return <div className={classnames('divider', props.className ? props.className : '')}>{props.children}</div>;
}

export interface IFormLabelProps {
    children: React.ReactNode;
    clarification?: string;
    isRequired?: boolean;
    tooltipText?: React.ReactNode;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormLabel(props: IFormLabelProps) {
    return (
        <label className={`form-label${props.clarification ? ' label-large' : ''}`}>
            {props.children}
            {props.isRequired && <span className="required">*</span>}
            {props.tooltipText && <InfoTooltip text={props.tooltipText} />}
            {props.clarification && <span className="clarification">{props.clarification}</span>}
        </label>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function InputGroup(props: { children: React.ReactNode; hasErrors?: boolean; stacked?: boolean }) {
    return (
        <div className={classnames('input-group--multiple', { 'has-danger': props.hasErrors })}>
            <InputContainer stacked={props.stacked}>{props.children}</InputContainer>
        </div>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function InputContainer(props: {
    children: React.ReactNode;
    medium?: boolean;
    small?: boolean;
    stacked?: boolean;
    verticalAlign?: boolean;
}) {
    let className = '';
    if (props.medium) {
        className += ' input-medium';
    } else if (props.small) {
        className += ' input-small';
    }
    if (props.verticalAlign) {
        className += ' vertical-align';
    }

    return (
        <div className={classnames(`input-container ${className}`, { 'd-block': props.stacked })}>{props.children}</div>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FullWidthInputGroup(props: { children: React.ReactNode }) {
    return <div className="input-group input-group--fullwidth">{props.children}</div>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function MediumInputText(props: {
    disabled?: boolean;
    error?: string;
    id?: string;
    onChange: (value: string) => void;
    onDoValidate?: () => void;
    placeholder?: string;
    value: string;
}) {
    return (
        <TextInput
            className="input-medium"
            {...props}
        />
    );
}

export interface ISmallNullableInputTextProps {
    autoFocus?: boolean;
    automation?: string;
    descriptionText?: string;
    descriptionTextClassName?: string;
    disableClickPropagation?: boolean;
    disabled?: boolean;
    error?: string;
    id?: string;
    onChange: (value: string | null) => void;
    onDoValidate?: () => void;
    placeholder?: string;
    tabIndex?: number;
    value: string | null;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function SmallNullableInputText(props: ISmallNullableInputTextProps) {
    return (
        <NullableTextInput
            className={classnames('input-small', props.automation ? 'automation-' + props.automation : '')}
            {...props}
        />
    );
}

export function DangerAlertWhenErrorCode(props: { errorCode?: string }) {
    if (props.errorCode) {
        return <Alert appearance="warning">{translateKey('errorCodes.' + props.errorCode)}</Alert>;
    } else {
        return null;
    }
}

export function DangerAlertWhenErrorMessage(props: { errorMessage?: string }) {
    if (props.errorMessage) {
        return <Alert appearance="warning">{props.errorMessage}</Alert>;
    }

    return null;
}

interface ISuccessFlashWhenSavedProps {
    message?: string;
    savedCounter: number;
}

interface ISuccessFlashWhenSavedState {
    lastSaved: number;
    show: boolean;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export class SuccessFlashWhenSaved extends Component<ISuccessFlashWhenSavedProps, ISuccessFlashWhenSavedState> {
    private timeout: number;

    constructor(props: ISuccessFlashWhenSavedProps, context?: any) {
        super(props, context);
        this.state = {
            lastSaved: props.savedCounter,
            show: false,
        };
    }

    public async componentDidUpdate(prevProps: Readonly<ISuccessFlashWhenSavedProps>) {
        if (this.props.savedCounter > prevProps.savedCounter) {
            await this.setState({ show: true });
            if (this.timeout) {
                clearTimeout(this.timeout);
            }
            this.timeout = window.setTimeout(async () => await this.setState({ show: false }), 10000);
            await this.setState({ lastSaved: this.props.savedCounter });
        }
    }

    public render() {
        if (this.state.show) {
            return (
                <Alert appearance="success">
                    {this.props.message ? this.props.message : translateKey('common.savedData')}
                </Alert>
            );
        } else {
            return null;
        }
    }
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function EditButton(props: { onClick?: () => void; to?: string }) {
    return (
        <ReactRouterLink
            className="icon-link"
            onClick={props.onClick}
            to={props.to ? props.to : '#'}
        >
            <Icon icon="edit" />
        </ReactRouterLink>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function ViewButton(props: { onClick?: () => void; to?: string }) {
    return (
        <ReactRouterLink
            className="icon-link"
            onClick={props.onClick}
            to={props.to ? props.to : '#'}
        >
            <Icon icon="eye" />
        </ReactRouterLink>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function DeleteButton(props: { onClick?: () => void }) {
    return (
        <Link
            icon="icon-link"
            onClick={(e) => {
                e.preventDefault();
                if (props.onClick) {
                    props.onClick();
                }
            }}
            to="#"
        >
            <Icon icon="exit" />
        </Link>
    );
}
/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function DropdownLink(props: { labelLess: string; labelMore: string; onClick: () => void; open: boolean }) {
    return (
        <Link
            collapse="dropdown-link"
            color="muted"
            onClick={(e) => {
                e.preventDefault();
                if (props.onClick) {
                    props.onClick();
                }
            }}
            to="#"
        >
            {props.open ? (
                <>
                    <Icon
                        color={IconColor.muted}
                        icon="caret-up"
                    />
                    {props.labelLess}
                </>
            ) : (
                <>
                    <Icon
                        color={IconColor.muted}
                        icon="caret"
                    />{' '}
                    {props.labelMore}
                </>
            )}
        </Link>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function ClickableRowTable(props: { children: React.ReactNode; className?: string }) {
    return (
        <table className={classnames('table table-hover', props.className ? props.className : '')}>
            {props.children}
        </table>
    );
}

type InfoTooltipProps = {
    text: React.ReactNode;
    tooltipHeader?: string;
};

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function InfoTooltip(props: InfoTooltipProps) {
    return (
        <Tooltip
            content={
                <>
                    <h4 className="text-white">{props.tooltipHeader}</h4>
                    {props.text}
                </>
            }
        >
            <DSIcon
                color="primary"
                icon="information"
                sx={{ ml: 2 }}
            />
        </Tooltip>
    );
}

interface IAnchorButtonProps {
    children?: React.ReactNode;
    className?: string;
    color: 'primary' | 'secondary' | 'tertiary' | 'transparent';
    large?: boolean;
    openInNewTab?: boolean;
    outlined?: boolean;
    to: string;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function AnchorButton(props: IAnchorButtonProps) {
    let className = `${props.className} btn`;

    if (props.color) {
        className += ` btn-${props.color}`;
    }

    if (props.large) {
        className += ' btn-lg';
    }

    if (props.outlined) {
        className += ' outlined';
    }

    return (
        <a
            className={className}
            href={props.to}
            rel="noreferrer"
            target={props.openInNewTab ? '_blank' : undefined}
        >
            {props.children}
        </a>
    );
}

interface IAnchorLink {
    children?: React.ReactNode;
    openInNewTab?: boolean;
    paddingDirection?: 'l' | 'r';
    paddingValue?: number;
    to: string;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function AnchorLink(props: IAnchorLink) {
    let className = 'secondary';
    if (props.paddingValue) {
        className += ` p${props.paddingDirection}-${props.paddingValue}`;
    }

    return (
        <a
            className={className}
            href={props.to}
            rel="noreferrer"
            target={props.openInNewTab ? '_blank' : undefined}
        >
            {props.children}
        </a>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function PrintButton(props: { disabled?: boolean; onClick?: () => void }) {
    return (
        <Button
            color="transparent"
            disabled={props.disabled}
            icon="print"
            iconColor={IconColor.secondary}
            large
            onClick={props.onClick}
        >
            {translateKey('common.print')}
        </Button>
    );
}

interface IButtonProps {
    active?: 'active' | 'inactive';
    automation?: string;
    badgeCount?: number;
    buttonRef?: React.Ref<HTMLButtonElement>;
    careplan?: boolean;
    children?: React.ReactNode;
    className?: string;
    color: 'primary' | 'secondary' | 'tertiary' | 'transparent';
    disabled?: boolean;
    icon?: string;
    iconColor?: IconColor;
    isSensitiveData?: boolean;
    large?: boolean;
    left?: boolean;
    onClick?: () => void;
    outlined?: boolean;
    right?: boolean;
    submit?: boolean;
    tabIndex?: number;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function Button(props: IButtonProps) {
    let className = 'btn';

    if (props.color) {
        className += ` btn-${props.color}`;
    }

    if (props.large) {
        className += ' btn-lg';
    }

    if (props.careplan) {
        className += ' btn-careplan';
    }

    if (props.left) {
        className += ' float-left';
    }

    if (props.right) {
        className += ' float-right';
    }

    if (props.outlined) {
        className += ' outlined';
    }

    if (props.active) {
        if (props.active === 'active') {
            className += ' active';
        } else if (props.active === 'inactive') {
            className += ' inactive';
        }
    }

    return (
        <button
            className={classnames(
                className,
                props.className || '',
                { disabled: props.disabled },
                props.automation ? 'automation-' + props.automation : ''
            )}
            data-automation-test={props.automation}
            data-dd-privacy={props.isSensitiveData ? 'mask' : 'allow'}
            disabled={props.disabled}
            onClick={() => !props.disabled && props.onClick && props.onClick()}
            ref={props.buttonRef}
            tabIndex={props.tabIndex}
            type={props.submit ? 'submit' : 'button'}
        >
            {props.icon && (
                <Icon
                    color={props.iconColor}
                    icon={props.icon}
                />
            )}
            {props.children}
            {props.badgeCount !== undefined && <span className="badge badge-notify">{props.badgeCount}</span>}
        </button>
    );
}

interface IIconButtonProps {
    className?: string;
    disabled?: boolean;
    icon: string;
    inputType?: 'submit' | 'button';
    onClick?: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => void;
    solid?: boolean;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function IconButton(props: IIconButtonProps) {
    let className = 'btn-icon';

    if (props.className) {
        className += ` ${props.className}`;
    }

    if (props.icon) {
        className += ` btn-${props.icon}`;
    }

    if (props.solid) {
        className += ' btn-solid';
    }

    return (
        <button
            className={className}
            disabled={props.disabled}
            onClick={props.onClick}
            type={props.inputType}
        >
            <Icon icon={props.icon} />
        </button>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function ArrowButton(props: {
    automation?: string;
    children: React.ReactNode;
    color: 'primary' | 'secondary';
    disabled?: boolean;
    large?: boolean;
    onClick?: () => void;
    outlined?: boolean;
    submit?: boolean;
}) {
    let className = 'btn btn-arrow';

    if (props.automation) {
        className += ` automation-${props.automation}`;
    }
    if (props.color) {
        className += ` btn-${props.color}`;
    }
    if (props.large) {
        className += ' btn-lg';
    }
    if (props.submit) {
        className += ' btn-submit';
    }
    if (props.disabled) {
        className += ' disabled';
    }

    if (props.outlined) {
        className += ' outlined';
    }

    return (
        <button
            className={className}
            disabled={props.disabled}
            onClick={props.onClick}
            type={props.submit ? 'submit' : 'button'}
        >
            <Icon icon="caret-right" />
            {props.children}
        </button>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function HeaderButton(props: {
    automation?: string;
    badgeCount?: number;
    children: React.ReactNode;
    color?: 'primary' | 'secondary';
    disabled?: boolean;
    onClick?: () => void;
    selected: boolean;
}) {
    let className = 'btn btn-header btn-secondary';
    className += props.selected ? ' selected' : ' deselected';
    if (props.color) {
        className += ` btn-${props.color}`;
    }

    return (
        <button
            className={classnames(className, props.automation ? 'automation-' + props.automation : '', {
                disabled: props.disabled,
            })}
            data-automation-test={props.automation}
            disabled={props.disabled}
            onClick={props.onClick}
            type="button"
        >
            {props.children}
            {props.badgeCount !== undefined && <span className="badge badge-notify">{props.badgeCount}</span>}
        </button>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function CancelButton(props: { disabled?: boolean; to: string }) {
    const navigate = useNavigate();

    return (
        <Button
            color="secondary"
            disabled={props.disabled}
            large
            left
            onClick={() => navigate(props.to)}
            outlined
        >
            {translateKey('common.cancel')}
        </Button>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function SaveSubmit(props: { disabled?: boolean; name?: string }) {
    return (
        <ArrowButton
            automation="saveSubmit"
            color="secondary"
            disabled={props.disabled}
            large
            submit
        >
            {translateKey(props.name ? props.name : 'common.save')}
        </ArrowButton>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function SaveButton(props: { disabled?: boolean; name?: string; onClick: () => void }) {
    return (
        <ArrowButton
            color="secondary"
            disabled={props.disabled}
            large
            onClick={props.onClick}
        >
            {translateKey(props.name ? props.name : 'common.save')}
        </ArrowButton>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function ContinueButton(props: {
    automation?: string;
    disabled?: boolean;
    onClick: () => void;
    submit?: boolean;
}) {
    return (
        <ArrowButton
            automation={props.automation}
            color="secondary"
            disabled={props.disabled}
            large
            onClick={props.onClick}
            submit={props.submit !== undefined ? props.submit : true}
        >
            {translateKey('common.proceed')}
        </ArrowButton>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function Feedback(props: { error?: string }) {
    if (props.error) {
        return <div className="form-control-feedback">{getValidationErrorTranslation(props.error)}</div>;
    } else {
        return null;
    }
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function AdminTable(props: {
    children: React.ReactNode;
    ellipsisText?: boolean;
    fullWidth?: boolean;
    tableLayoutFixed?: boolean;
}) {
    return (
        <table
            className={classnames(
                'table admin',
                props.tableLayoutFixed ? 'fixed' : '',
                props.fullWidth ? 'fullWidth' : ''
            )}
        >
            {props.children}
        </table>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function ListTable(props: { children: React.ReactNode }) {
    return <table className="list-table">{props.children}</table>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function TableHead(props: { children: React.ReactNode; narrow?: boolean }) {
    return <thead className={props.narrow ? 'narrow' : ''}>{props.children}</thead>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function TableRow(props: { children: React.ReactNode; highlight?: boolean }) {
    return <tr className={props.highlight ? 'active' : ''}>{props.children}</tr>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function TableHeader(props: {
    align?: 'right';
    children?: React.ReactNode;
    className?: string;
    color?: string;
    colspan?: number;
    onClick?: (event: React.MouseEvent<HTMLTableHeaderCellElement>) => void;
    width?: number;
}) {
    const tdStyles = {
        width: `${props.width}%`,
        textAlign: props.align ? props.align : undefined,
        color: props.color,
    };

    return (
        <th
            className={props.className || ''}
            colSpan={props.colspan || 1}
            onClick={props.onClick}
            style={tdStyles}
        >
            {props.children}
        </th>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function TableBody(props: { children: React.ReactNode; className?: string }) {
    return <tbody className={props.className || ''}>{props.children}</tbody>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function TableData(props: {
    align?: 'right';
    bold?: boolean;
    children: React.ReactNode;
    className?: string;
    color?: 'grey';
    colspan?: number;
    onClick?: (event: React.MouseEvent<HTMLTableHeaderCellElement>) => void;
    style?: CSSProperties;
}) {
    const classNames = classnames(
        props.className ? props.className : '',
        props.color ? 'lighten' : '',
        props.bold ? 'font-weight-bold' : ''
    );

    return (
        // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
        <td
            className={classNames}
            colSpan={props.colspan || 1}
            onClick={props.onClick}
            style={{ ...(props.align ? { textAlign: 'right' } : {}), ...props.style }}
        >
            <span>{props.children}</span>
        </td>
    );
}

export interface IBreadcrumbItem {
    text: string;
    url?: string;
}
/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function Breadcrumb(props: { items: IBreadcrumbItem[] }) {
    return (
        <div className="col-xl-24">
            <ol className="breadcrumb">
                {props.items.map((item, i) => (
                    <li
                        className="breadcrumb-item"
                        key={i}
                    >
                        {item.url ? <Link to={item.url}>{item.text}</Link> : item.text}
                    </li>
                ))}
            </ol>
        </div>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function Header(props: {
    actionButtons?: React.ReactElement<{ key: number }>[];
    content: React.ReactNode;
    extraButton?: JSX.Element;
    filterButtons?: React.ReactElement<{ key: number }>[];
    headerClassName?: string;
    onSearch?: () => void;
    patientButton?: JSX.Element;
    searchButton?: JSX.Element;
    searchFields?: React.ReactElement<{ key: number }>[];
    smallHeader?: boolean;
    style?: React.CSSProperties;
    tooltipText?: React.ReactNode;
}) {
    const search = (
        <form
            className="flex"
            onSubmit={(e) => {
                e.preventDefault();
                if (props.onSearch) {
                    props.onSearch();
                }
            }}
        >
            {props.searchFields && (
                <div className="form-group flex col">
                    {props.searchFields.map((searchBox, i) => {
                        return (
                            <div
                                className="input-container d-block"
                                key={i}
                            >
                                {React.cloneElement(searchBox, { key: i })}
                            </div>
                        );
                    })}
                    {props.searchButton || (
                        <button
                            className="btn btn-search"
                            type="submit"
                        >
                            <Icon icon="search" />
                        </button>
                    )}
                </div>
            )}

            {props.actionButtons && (
                <span className="float-right">
                    {props.actionButtons.map((actionButton, i) => React.cloneElement(actionButton, { key: i }))}
                </span>
            )}
        </form>
    );

    let divSizeClassName;
    if (props.patientButton) {
        divSizeClassName = 'col-md-12';
    } else if (
        props.actionButtons ||
        props.extraButton ||
        props.filterButtons ||
        props.onSearch ||
        props.searchFields
    ) {
        divSizeClassName = 'col-md-10';
    } else {
        divSizeClassName = 'col-md-24';
    }

    return (
        <header
            className={props.headerClassName || ''}
            style={{ ...props.style }}
        >
            <div className="row">
                <div className={divSizeClassName}>
                    {React.isValidElement(props.content) ? (
                        props.content
                    ) : (
                        <h1 className={props.smallHeader ? 'small' : ''}>{props.content}</h1>
                    )}
                    {props.tooltipText && <InfoTooltip text={props.tooltipText} />}
                    {props.patientButton}
                </div>
                <div className={props.patientButton ? 'col-md-12' : 'col-md-14'}>{props.extraButton || search}</div>
            </div>
            {props.filterButtons && (
                <div className="row">
                    <div className="col-lg-14 col-xl-10">
                        <div className="btn-container">
                            {props.filterButtons.map((button, i) => React.cloneElement(button, { key: i }))}
                        </div>
                    </div>
                    <div className="col-lg-10 col-xl-14">{props.extraButton && search}</div>
                </div>
            )}
        </header>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function SubHeader(props: { text: string }) {
    return (
        <div className="justify-content-md-center clearfix">
            <h2>{props.text}</h2>
        </div>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function HeaderGroup(props: {
    alignment?: 'left' | 'right' | 'center';
    className?: string;
    header: string;
    text?: string;
}) {
    let className = 'header-group';
    if (props.alignment) {
        className += ` header-group--${props.alignment}`;
    }

    return (
        <div className={classnames(className, props.className || '')}>
            <h1>{props.header}</h1>
            <h2>{props.text}</h2>
        </div>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function HeroTitle(props: { text: string }) {
    return <h1 className="hero-title">{props.text}</h1>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function SecondHeader(props: { status?: 'warning' | 'danger' | 'success'; text: string }) {
    return <h2 className={classnames({ [`has-${props.status}`]: !!props.status })}>{props.text}</h2>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function ThirdHeader(props: { status?: 'warning' | 'danger' | 'success'; text: string }) {
    return <h3 className={classnames({ [`has-${props.status}`]: !!props.status })}>{props.text}</h3>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function HeaderDivider(props: {
    display?: 'flex' | 'inline-block';
    icon?: 'checked' | 'info';
    status?: 'warning' | 'success';
    statusText?: string;
    text: string;
    tooltipText?: React.ReactNode;
}) {
    return (
        <h3 className={classnames('title-divider', props.display)}>
            {props.text}
            <div className={classnames(props.status ? `${props.status}` : '')}>
                {props.icon ? <Icon icon={props.icon} /> : null}
                {props.statusText}
                {props.tooltipText && <InfoTooltip text={props.tooltipText} />}
            </div>
        </h3>
    );
}

interface IButtonContainerBase {
    align?: 'center';
    alignLeft?: boolean;
    alignRight?: boolean;
    className?: string;
    hideButtons?: boolean;
    noMargin?: boolean;
    noMarginTop?: boolean;
    text?: string;
    wrapContent?: boolean;
}

interface IButtonContainerButtons extends IButtonContainerBase {
    buttons: React.ReactElement<{ key: number | string }>[];
    children?: never;
}

interface IButtonContainerChildren extends IButtonContainerBase {
    buttons?: never;
    children: React.ReactNode;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function ButtonContainer(props: IButtonContainerButtons | IButtonContainerChildren) {
    if (props.hideButtons) {
        return null;
    }
    let className = 'btn-container';

    if (props.align) {
        className += ' align-center';
    }

    if (props.alignRight) {
        className += ' align-right';
    } else if (props.alignLeft) {
        className += ' align-left';
    }

    if (props.wrapContent) {
        className += ' wrap-content';
    }
    if (props.noMargin) {
        className += ' no-margin';
    }
    if (props.noMarginTop) {
        className += ' mt-0 mb-3';
    }

    if (props.buttons) {
        return (
            <div className={classnames(className, props.className || '')}>
                {props.buttons &&
                    props.buttons.map((button, i) =>
                        React.cloneElement(button, { key: button.key ? String(button.key) : i })
                    )}
            </div>
        );
    } else {
        return <div className={classnames(className, props.className || '')}>{props.children}</div>;
    }
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormAnamnesisButtons(props: { buttons: React.ReactElement<{ key: number }>[] }) {
    return (
        <div className="btn-group__anamnesis-buttons">
            {props.buttons && props.buttons.map((button, i) => React.cloneElement(button, { key: i }))}
        </div>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function HeaderSearchBox(props: {
    label?: string;
    onChange: (value: string) => void;
    text: string;
    value: string | null | undefined;
}) {
    return (
        <>
            {props.label && <FormLabel>{props.label}</FormLabel>}
            <input
                className="form-control form-search"
                onChange={(e) => props.onChange(e.target.value)}
                placeholder={props.text}
                title={props.text}
                type="search"
                value={props.value ?? ''}
            />
        </>
    );
}

export interface ISelectOption {
    text: string;
    value: string;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function HeaderSearchSelectBox(props: {
    error?: string;
    label?: string;
    onChange: (value: any) => void;
    placeholder?: string;
    selectOptions: ISelectOption[];
    value: string | null;
}) {
    const noSelection = !props.value;
    const selectClassName = classnames('form-control form-dropdown', { pristine: noSelection });

    return (
        <>
            {props.label && <FormLabel>{props.label}</FormLabel>}
            <select
                className={selectClassName}
                key="select"
                onChange={(e) => props.onChange(e.target.value)}
                value={props.value || ''}
            >
                {props.placeholder && <option value={''}>{props.placeholder}</option>}
                {props.selectOptions.map((option, i) => (
                    <option
                        key={i}
                        value={option.value}
                    >
                        {option.text}
                    </option>
                ))}
            </select>
            <Feedback error={props.error} />
        </>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export enum IconColor {
    danger = 'danger',
    dangerBorder = 'danger-border',
    info = 'info',
    inverted = 'inverted',
    muted = 'muted',
    primary = 'primary',
    secondary = 'secondary',
    success = 'success',
    white = 'white',
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
function renderIcon(props: { className?: string; color?: IconColor; icon: string; large?: boolean }) {
    const icon = props.icon.toLowerCase();
    let classNames = `icon icon-${icon}`;
    if (props.large) {
        classNames += ' large';
    }
    if (props.color) {
        classNames += ' ' + props.color;
    }

    return (
        <i className={classnames(classNames, props.className || '')}>
            <svg
                className={'icon-' + icon}
                viewBox="0 0 100 100"
            >
                <use xlinkHref={'/assets/images/svg-sprite.svg#icon-' + icon}></use>
            </svg>
        </i>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function Icon(props: { className?: string; color?: IconColor; icon: string }) {
    return renderIcon(props);
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function IconOnClick(props: { icon: string; onClick: () => void }) {
    return (
        <a
            onClick={(e) => {
                e.preventDefault();
                props.onClick();
                e.stopPropagation();
            }}
            style={{ cursor: 'pointer' }}
        >
            <Icon icon={props.icon} />
        </a>
    );
}

interface ILinkProps {
    automation?: string;
    children: React.ReactNode;
    classNames?: string[];
    collapse?: 'dropdown-link';
    color?: 'primary' | 'secondary' | 'tertiary' | 'muted';
    icon?: string;
    large?: boolean;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
    openInNewTab?: boolean;
    outlined?: boolean;
    right?: boolean;
    to: string;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function Link(props: ILinkProps) {
    return (
        <ReactRouterLink
            className={classnames({
                muted: props.color === 'muted',
                secondary: props.color === 'secondary',
                tertiary: props.color === 'tertiary',
                'dropdown-link': props.collapse === 'dropdown-link',
            })}
            onClick={props.onClick}
            target={props.openInNewTab ? '_blank' : undefined}
            to={props.to}
        >
            {props.children}
        </ReactRouterLink>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function LinkOnClick(props: {
    children: React.ReactNode;
    className?: string;
    disabled?: boolean;
    noClassName?: boolean;
    onClick: () => void;
}) {
    if (props.disabled) {
        return (
            <a
                className={classnames(props.className, props.noClassName ? '' : 'link')}
                style={{ color: '#A69A91', textDecoration: 'underline' }}
            >
                {props.children}
            </a>
        );
    }

    return (
        <a
            className={classnames(props.className, props.noClassName ? '' : 'link')}
            href="#"
            onClick={(e) => {
                e.preventDefault();
                props.onClick();
                e.stopPropagation();
            }}
        >
            {props.children}
        </a>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormLink(props: { children: React.ReactNode; to: string }) {
    return (
        <ReactRouterLink
            className="link"
            to={props.to}
        >
            {props.children}
        </ReactRouterLink>
    );
}

export class LinkButton extends React.Component<ILinkProps, {}> {
    public render() {
        let classNames = [];
        if (this.props.classNames) {
            classNames = this.props.classNames;
            classNames.push('btn');
        } else {
            classNames = ['btn', 'btn-large'];
        }

        if (this.props.right) {
            classNames.push('float-right');
        }

        if (this.props.outlined) {
            classNames.push('outlined');
        }

        if (this.props.color === 'secondary') {
            classNames.push('btn-secondary');
        } else if (this.props.color === 'tertiary') {
            classNames.push('btn-tertiary');
        } else {
            classNames.push('btn-primary');
        }

        return (
            <ReactRouterLink
                className={classnames(classNames, this.props.automation ? 'automation-' + this.props.automation : '')}
                onClick={this.props.onClick}
                to={this.props.to}
            >
                {this.props.icon && <Icon icon={this.props.icon} />}
                {this.props.children}
            </ReactRouterLink>
        );
    }
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function LoaderImg() {
    return (
        <svg viewBox="0 0 117.293 117.292">
            <defs>
                <linearGradient
                    id="left"
                    x1="48.7646216%"
                    x2="30.9646012%"
                    y1="72.5283907%"
                    y2="36.9283498%"
                >
                    <stop
                        offset="20%"
                        stopColor="#E871CA"
                    ></stop>
                    <stop
                        offset="100%"
                        stopColor="#EA3D2F"
                    ></stop>
                </linearGradient>
                <linearGradient
                    id="right"
                    x1="63.8377099%"
                    x2="43.0482138%"
                    y1="52.6736691%"
                    y2="11.0962384%"
                >
                    <stop
                        offset="0%"
                        stopColor="#F4F4F2"
                    ></stop>
                    <stop
                        offset="70%"
                        stopColor="#F4F4F2"
                    ></stop>
                </linearGradient>
                <linearGradient
                    id="bottom"
                    x1="61.6626738%"
                    x2="-0.207173489%"
                    y1="68.3308609%"
                    y2="37.396201%"
                >
                    <stop
                        offset="0%"
                        stopColor="#E871CA"
                    ></stop>
                    <stop
                        offset="51%"
                        stopColor="#F086F0"
                    ></stop>
                </linearGradient>
                <linearGradient
                    id="top"
                    x1="75.4512204%"
                    x2="36.5153931%"
                    y1="50.2255017%"
                    y2="30.757754%"
                >
                    <stop
                        offset="0%"
                        stopColor="#F6E9D4"
                    ></stop>
                    <stop
                        offset="97%"
                        stopColor="#F19E41"
                    ></stop>
                </linearGradient>
            </defs>
            <g
                fillRule="evenodd"
                stroke="none"
                strokeWidth="1"
            >
                <g
                    fill="none"
                    fillRule="nonzero"
                >
                    <polygon
                        fill="url(#left)"
                        points="58.646 58.646 0 117.292 0 0"
                    ></polygon>
                    <polygon
                        fill="url(#right)"
                        points="58.646 58.646 117.293 0 117.293 117.292"
                    ></polygon>
                    <polygon
                        fill="url(#bottom)"
                        points="58.646 58.646 117.293 117.292 0 117.292"
                    ></polygon>
                    <polygon
                        fill="url(#top)"
                        points="58.646 58.646 117.293 0 0 0"
                    ></polygon>
                </g>
            </g>
        </svg>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function Loader(props: {
    centered?: boolean;
    fixedCentered?: boolean;
    horizontalCentered?: boolean;
    isBlocking?: boolean;
    large?: boolean;
}) {
    const loader = (
        <div
            className={classnames('loader', {
                'is-fixed': props.fixedCentered,
                centered: props.centered,
                large: props.large,
                'horizontal-centered': props.horizontalCentered,
            })}
        >
            <LoaderImg />
        </div>
    );

    return props.isBlocking ? <div className="loader-container-fullscreen">{loader}</div> : loader;
}

interface IPerson {
    id?: string;
    nameFamily?: string;
    nameFamily2?: string | null;
    nameGiven?: string | null;
    nameInfix?: string | null;
    nameInfix2?: string | null;
    nameInitials?: string | null;
    nameUsual?: string | null;
}

export function FullName(props: IPerson) {
    return <>{getDisplayName(props)}</>;
}

export function NullableBoolean(props: { value?: boolean | null }) {
    if (props.value === undefined || props.value === null) {
        return null;
    }

    return <ShowYesNo value={props.value} />;
}

export function ShowYesNo(props: { value: boolean }) {
    return <>{props.value ? translateKey('common.yes') : translateKey('common.no')}</>;
}

export function Currency(props: { value?: number | null }) {
    if (props.value === undefined || props.value === null) {
        return null;
    }

    const globalize = getGlobalize();
    const formatter = globalize.currencyFormatter('EUR');

    return <>{formatter(props.value / 100)}</>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function FormMessage(props: { header?: string; text?: string }) {
    return (
        <div className="form-group">
            <div className="input-columns">
                <div className="row">
                    <div className="columns col-md-24">
                        <ul className="form-list">
                            <li>
                                {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                                <label className="form-label">&nbsp;</label>
                                <p>
                                    <strong>{props.header}</strong>
                                    <br />
                                    {props.text}
                                </p>
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
}

export function OverlapMessage(props: {
    endDate: string | null;
    link: string | null;
    message: string;
    startDate: string;
}) {
    const translate = getLanguage();

    return (
        <Alert
            appearance="warning"
            hasIcon={true}
        >
            <Box
                alignItems="flex-start"
                display="flex"
                flexWrap="wrap"
                gap={4}
                justifyContent="space-between"
                width="100%"
            >
                <Box>
                    <Alert.Text
                        as="p"
                        fontWeight="bold"
                        mb={2}
                        size="md"
                    >
                        {props.message}
                    </Alert.Text>
                    <Alert.Text
                        as="p"
                        size="md"
                    >
                        {translate.common.startDate}: <NullableFormattedDate value={props.startDate} /> t/m{' '}
                        {translate.common.endDate}:{' '}
                        {props.endDate ? <NullableFormattedDate value={props.endDate} /> : '-'}
                    </Alert.Text>
                    {props.link && (
                        <Alert.Link
                            to={props.link}
                            variant="link"
                        >
                            <Alert.Button style={{ marginTop: 12 }}>{translate.supervisors.editMessage}</Alert.Button>
                        </Alert.Link>
                    )}
                </Box>
            </Box>
        </Alert>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function ListHeader(props: { children: React.ReactNode; className?: string }) {
    return <h4 className={props.className || ''}>{props.children}</h4>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function OrderedList(props: { children: React.ReactNode }) {
    return <ol>{props.children}</ol>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function ItemBold(props: { children: React.ReactNode }) {
    return <span className="font-weight-bold">{props.children}</span>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function AdminProcessNullableFormattedDatetime(props: { value?: Date | string | null }) {
    return (
        <span className="date-time-stamp">
            <NullableFormattedDatetime value={props.value} />
        </span>
    );
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function InlineElement(props: { children: React.ReactNode; className?: string }) {
    return <span className={props.className ? props.className : ''}>{props.children}</span>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function InlineElementTextColor(props: {
    children: React.ReactNode;
    className?: string;
    color: 'danger' | 'info' | 'success' | 'emergency';
}) {
    return <span className={`has-text-${props.color} ${props.className || ''}`}>{props.children}</span>;
}

/**
 * @deprecated use ksyos/design-system instead. Remove if no longer used.
 */
export function Paragraph(props: { children: React.ReactNode; className?: string }) {
    return <p className={props.className ? props.className : ''}>{props.children}</p>;
}

// @TODO: move to Design System [CS-26382]
export function TextWithMatchingTermInBold(props: {
    customMatchStyling?: React.CSSProperties;
    searchTerm?: string | null;
    text?: string | null;
}) {
    if (props.searchTerm && props.text) {
        const splitSearchTerm = props.searchTerm.trim().split(' ');
        const splitText = props.text.split(' ');

        return (
            <>
                {splitText.map((word, key) => {
                    const match = splitSearchTerm.find((term) => word.toLowerCase().includes(term.toLowerCase()));
                    if (match) {
                        const escapedMatch = match.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
                        const startBoldSubstring = word.toLowerCase().search(escapedMatch.toLowerCase());
                        const endBoldSubstring = startBoldSubstring + match.length;

                        return (
                            <React.Fragment key={key}>
                                {' ' + word.substring(0, startBoldSubstring)}
                                <b style={props.customMatchStyling}>
                                    {word.substring(startBoldSubstring, endBoldSubstring)}
                                </b>
                                {word.substring(endBoldSubstring, word.length) + ' '}
                            </React.Fragment>
                        );
                    }

                    return <React.Fragment key={key}>{' ' + word + ' '}</React.Fragment>;
                })}
            </>
        );
    }

    return <>{props.text}</>;
}

export const StyledBorderAndPadding = styled.div`
    border: 1px solid #ecece6;
    padding: 1rem;
    margin-bottom: 1rem;
`;

// temporarily export all members that were peviously defined here
export { SteppedSliderInput as SteppedSlider } from '../fields/SteppedSlider';
export { DateInput } from '../fields/Date';
export { DropdownInput as Dropdown } from '../fields/Dropdown';
export { DropdownAsyncLoadInput as DropdownSearch } from '../fields/DropdownAsyncLoad';
export { EnumDropdownInput as EnumDropdown } from '../fields/EnumDropdown';
export { GroupedDropdownInput as GroupedDropdown } from '../fields/GroupedDropdown';
export { PasswordInput as InputPassword } from '../fields/Password';
export { UsernameInput as InputLogin } from '../fields/Username';
export { TextInput as InputText } from '../fields/Text';
export { NullableTextInput as NullableInputText } from '../fields/NullableText';
export { NewNullableTextInput as NewNullableInputText } from '../fields/NewNullableText';
export { CurrencyInput as InputCurrency } from '../fields/Currency';
export { ReadonlyTextInput as ReadOnlyInputText } from '../fields/readOnlyText/ReadonlyText';
export { NumberInput as InputNumber } from '../fields/Number';
export { RadioInput as InputRadio } from '../fields/Radio';
export { RadioGroupInput as InputRadioGroup } from '../fields/RadioGroup';
export { CheckboxInput as InputCheckbox } from '../fields/Checkbox';
export { TextAreaInput as InputTextArea } from '../fields/TextArea';
export { SwitchInput as InputSwitch } from '../fields/Switch';
export { TriStateInput as InputTriState } from '../fields/TriState';
export { TriStateWithTextInput, TriStateWithTextShow } from '../fields/TriStateWithText';
